/**
 * EdgeSelectorControls - Edge selection and azimuth controls for roof orientation
 * Used in roof section for solar panel alignment
 */

import React from 'react';

interface EdgeSelectorControlsProps {
  isSelectingEdge: boolean;
  onStartEdgeSelection: () => void;
  onStopEdgeSelection: () => void;
  selectedEdgeIndex?: number;
  edgeAzimuth?: number | null;
  manualAzimuth?: number | null;
  onManualAzimuthChange: (azimuth: number | null) => void;
}

const getCompassDirection = (az: number): string => {
  if (az >= 348.75 || az < 11.25) return 'N';
  if (az >= 11.25 && az < 33.75) return 'NNE';
  if (az >= 33.75 && az < 56.25) return 'NE';
  if (az >= 56.25 && az < 78.75) return 'ENE';
  if (az >= 78.75 && az < 101.25) return 'E';
  if (az >= 101.25 && az < 123.75) return 'ESE';
  if (az >= 123.75 && az < 146.25) return 'SE';
  if (az >= 146.25 && az < 168.75) return 'SSE';
  if (az >= 168.75 && az < 191.25) return 'S';
  if (az >= 191.25 && az < 213.75) return 'SSW';
  if (az >= 213.75 && az < 236.25) return 'SW';
  if (az >= 236.25 && az < 258.75) return 'WSW';
  if (az >= 258.75 && az < 281.25) return 'W';
  if (az >= 281.25 && az < 303.75) return 'WNW';
  if (az >= 303.75 && az < 326.25) return 'NW';
  return 'NNW';
};

export const EdgeSelectorControls: React.FC<EdgeSelectorControlsProps> = ({
  isSelectingEdge,
  onStartEdgeSelection,
  onStopEdgeSelection,
  selectedEdgeIndex,
  edgeAzimuth,
  manualAzimuth,
  onManualAzimuthChange,
}) => {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-700 uppercase">Edge & Azimuth</h4>

      {!isSelectingEdge ? (
        <>
          {/* Current Selection Display */}
          {selectedEdgeIndex !== undefined ? (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-700">
                Edge {selectedEdgeIndex + 1} selected
              </p>
              {edgeAzimuth !== null && edgeAzimuth !== undefined && (
                <p className="text-xs text-gray-700 mt-1">
                  Azimuth: <span className="font-bold">{edgeAzimuth}° {getCompassDirection(edgeAzimuth)}</span>
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-600 italic">No edge selected</p>
          )}

          {/* Select Edge Button */}
          <button
            onClick={onStartEdgeSelection}
            className="w-full px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300 rounded-lg text-sm font-semibold transition-colors"
          >
            Select Edge
          </button>

          {/* Manual Azimuth Input */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Manual Azimuth
            </label>
            <input
              type="number"
              value={manualAzimuth ?? ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                if (value === '') {
                  onManualAzimuthChange(null);
                } else {
                  const num = parseFloat(value);
                  if (!isNaN(num) && num >= 0 && num <= 360) {
                    onManualAzimuthChange(num);
                  }
                }
              }}
              placeholder="Auto (from edge)"
              className="w-full px-2 py-1 border-2 border-gray-300 rounded text-xs focus:outline-none focus:border-[#EBFD03]"
            />
            <p className="text-xs text-gray-500 mt-1">
              0° = N, 90° = E, 180° = S, 270° = W
            </p>
          </div>
        </>
      ) : (
        <>
          {/* Edge Selection Mode Active */}
          <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-700 mb-1">Select Edge Mode</p>
            <p className="text-xs text-gray-700">Click an edge on the polygon</p>
          </div>
          <button
            onClick={onStopEdgeSelection}
            className="w-full px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300 rounded-lg text-sm font-semibold transition-colors"
          >
            Done Selecting
          </button>
        </>
      )}
    </div>
  );
};
